import React, { useCallback, useEffect, useState } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import api from '../../services/api';
import { showError } from '../../services/alert';

const RelatorioIntegrado = () => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/relatorios/integrado');
      setDados(data);
    } catch (error) {
      console.error('Erro ao carregar relatório integrado', error);
      showError('Não foi possível carregar o relatório integrado.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const downloadPdf = async () => {
    try {
      const response = await api.get('/relatorios/integrado/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio-integrado.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Erro ao exportar PDF', error);
      showError('Falha ao exportar o PDF do relatório integrado.');
    }
  };

  return (
    <div className="d-flex" style={{ minHeight: '100vh' }}>
      <Sidebar />
      <div className="flex-grow-1">
        <Header />
        <div className="container mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Relatório Integrado</h4>
            <button className="btn btn-success" onClick={downloadPdf} disabled={loading}>
              <i className="bi bi-file-earmark-pdf me-2"></i>
              Baixar PDF
            </button>
          </div>

          {loading && <div className="alert alert-info">Carregando dados consolidados...</div>}

          {dados && !loading && (
            <div className="row g-4">
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header bg-success text-white">Resumo Financeiro</div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <strong>Receitas do mês</strong>
                        <p>{(dados.dashboard?.financeiro?.receitasMes ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                      </div>
                      <div className="col-md-4">
                        <strong>Despesas do mês</strong>
                        <p>{(dados.dashboard?.financeiro?.despesasMes ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                      </div>
                      <div className="col-md-4">
                        <strong>Saldo Atual</strong>
                        <p>{(dados.dashboard?.financeiro?.saldoAtual ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm">
                  <div className="card-header bg-success text-white">Agricultores</div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-striped table-sm">
                        <thead>
                          <tr>
                            <th>Nome</th>
                            <th className="text-end">Propriedades</th>
                            <th className="text-end">Área Total (ha)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.agricultores?.map((agricultor) => (
                            <tr key={agricultor.id}>
                              <td>{agricultor.nome}</td>
                              <td className="text-end">{agricultor.totalPropriedades}</td>
                              <td className="text-end">{agricultor.areaTotalHa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm">
                  <div className="card-header bg-success text-white">Propriedades</div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-striped table-sm">
                        <thead>
                          <tr>
                            <th>Propriedade</th>
                            <th>Agricultor</th>
                            <th className="text-end">Talhões</th>
                            <th className="text-end">Área Talhões (ha)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.propriedades?.map((propriedade) => (
                            <tr key={propriedade.id}>
                              <td>{propriedade.nome}</td>
                              <td>{propriedade.agricultorNome}</td>
                              <td className="text-end">{propriedade.talhoes}</td>
                              <td className="text-end">{propriedade.areaTalhoesHa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header bg-success text-white">Safras</div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-striped table-sm">
                        <thead>
                          <tr>
                            <th>Cultura</th>
                            <th>Talhão</th>
                            <th className="text-end">Área (ha)</th>
                            <th className="text-end">Produção (kg)</th>
                            <th className="text-end">Produtividade (kg/ha)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.safras?.map((safra) => (
                            <tr key={safra.id}>
                              <td>{safra.cultura}</td>
                              <td>{safra.talhaoNome}</td>
                              <td className="text-end">{safra.areaHa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                              <td className="text-end">{safra.producaoKg.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                              <td className="text-end">{safra.produtividadeKgHa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelatorioIntegrado;
